// The simulation. Framework-free and DOM-free: this exact module runs in the browser, in vitest,
// and on the server for replay verification.
//
// Invariants that must never be broken (see .claude/skills/bigdog-engine):
//   1. Fixed timestep. `step()` always advances exactly DT. It never sees a wall-clock delta.
//   2. Seeded RNG only. No Math.random() below this line.
//   3. No transcendentals. See the note in vec2.ts.
//   4. Deterministic iteration order. No Set/Map iteration in the hot path.

import {
  addScaled,
  clamp,
  closestPointOnSegment,
  copy,
  dist2,
  len,
  len2,
  limit,
  normalize,
  scale,
  set,
  segmentsCross,
  sub,
  vec,
  type Vec2,
} from './vec2'
import { SpatialGrid } from './grid'
import { StateHasher } from './hash'
import { makeRng, rngInt, rngRange } from './seed'
import {
  DEFAULT_CONFIG,
  type Faults,
  type InputEvent,
  type Level,
  type RunResult,
  type SimConfig,
} from './types'
import {
  balanceAlignment,
  commandTarget,
  isCommand,
  LIFT_ARM_COS,
  LIFT_ENTER_COS,
  type CommandType,
  type CommandWorld,
} from './commands'

/**
 * Fixed timestep rate. Determinism-critical — the sim advances in whole ticks at this rate and the
 * accumulator in loop.ts is built around it. Do not change this to alter game feel.
 */
export const TICK_HZ = 60

/**
 * THE FEEL KNOB. How much world-time passes per tick.
 *
 * Scaling time rather than individual constants is deliberate: every tuned ratio in the system is
 * load-bearing (wander terminal speed vs settleSpeed, dog force vs the acceleration clamp, cohesion
 * vs separation), and scaling time preserves all of them by construction. Trajectories keep their
 * exact shape; they just happen faster. Speeds stay physically legible in m/s too — the alternative
 * was a 42 m/s dog.
 *
 * Effective pace at 2.0: the dog runs the paddock's 120 m long axis in ~3.3 s of WALL CLOCK
 * (120 m ÷ 18 m/s = 6.7 world-seconds ÷ SIM_SPEED). Speeds in DEFAULT_CONFIG are world m/s;
 * MULTIPLY by SIM_SPEED to get what a player sees move across the paddock.
 *
 * Lowered 3.0 → 2.0, then **2.0 → 1.6 on playtest** ("dog runs too fast still"). Apparent speeds
 * across the two moves: a 54 m/s dog and a 48 m/s panicked sheep, then 36 and 32, now **22 and 19**.
 *
 * The dog could not be slowed on its own. `dogSpeed` is floored by a tested invariant — it must
 * outrun a panicked sheep or a spooked flock is unrecoverable — so at 2.0 the most it could give up
 * was 8%. Reaching 22 m/s apparent took the sheep caps down as well, and then time rescaled to hold
 * the ratios that every other constant is fitted to.
 *
 * **This is a pace choice, not a realism one, and the numbers say why.** A real kelpie sprints
 * 12-14 m/s and a real sheep 7-10, which needs SIM_SPEED near 0.8 — and the game does not survive
 * there. Swept against the per-template gate: 2.0 holds 280/280, 1.5 drops to 277/280, 1.0 to
 * 266/280, because runs stretch until the bot's own clock runs out (chicane's median goes 57.5 s at
 * 3.0 to 152.7 s at 1.5). 2.0 is the slowest value the gate carries.
 *
 * Two things to re-check if you change it:
 *   1. Tunnelling. Per-tick displacement is `maxSpeed × panicSpeedMultiplier × DT` and must stay
 *      under `sheepRadius`, or panicked sheep cross fences and get resolved to the wrong side.
 *      There is a test asserting this.
 *   2. Template par times, which are wall-clock seconds. Re-run `npm run bot` and reset them.
 */
export const SIM_SPEED = 1.6

/**
 * WORLD-time advanced per tick. Physics only.
 *
 * NOT the real-time budget for a tick — see TICK_INTERVAL. Conflating the two is a bug that has
 * already happened once: loop.ts used DT as its accumulator threshold, so raising SIM_SPEED to 3
 * made the loop demand 0.05 REAL seconds per tick. The sim ran at 20Hz instead of 60, the on-screen
 * clock ran at a third of real time, motion updated 20 times a second while rendering at 60, and
 * the intended 3× speed-up was silently cancelled out to exactly 1× (20 ticks × 3 = 60 world-units).
 * Headless replay stepped the sim directly and so never saw any of it.
 */
export const DT = SIM_SPEED / TICK_HZ

/**
 * REAL seconds per tick — the wall-clock budget the render loop schedules against.
 * Always 1/TICK_HZ, independent of SIM_SPEED.
 */
export const TICK_INTERVAL = 1 / TICK_HZ

/** How stress reshapes the flocking rules. Written into `out` — the tick loop allocates nothing. */
export interface FlockWeights {
  cohesion: number
  separation: number
  /** Multiplier on the contentment radius: below 1 the mob is content only when packed tighter. */
  content: number
}

/**
 * Pressure BUNCHES a flock, and only fright scatters it.
 *
 * Two regimes rather than a switch on `panicked`. Below the panic threshold, rising stress pulls the
 * mob together — cohesion up, personal space down, and the contentment radius in, so the disc they
 * are content to sit in genuinely shrinks. Cohesion is zero inside that radius, so without moving it
 * a pressed mob would pull no harder however large the weight got; tightening it is what bunching
 * actually IS. Past the threshold the same numbers hand over to the panic values and the mob comes
 * apart.
 *
 * The old model had only the second half — separation ×4 and cohesion ×0.25 the moment a sheep
 * panicked — so pressure of any kind made sheep spread out and stop holding together. That is
 * backwards about the animal (sheep bunch under threat, which is why herding works at all) and it
 * made the dog an instrument that could only ever scatter its own stock.
 *
 * A pure function, and separated out so the curve can be asserted directly. The behaviour it
 * produces is statistical — over 21 bot runs it took the mean number of separate mobs from 1.88 to
 * 1.58 — and a single-seed test of that is either slow or a coin toss.
 */
export function flockWeights(
  stress: number,
  panicked: boolean,
  cfg: SimConfig,
  out: FlockWeights,
): void {
  let bunch: number
  let scatter: number
  if (panicked) {
    const span = 1 - cfg.panicEnter
    scatter = span > 0 ? clamp((stress - cfg.panicEnter) / span, 0, 1) : 1
    bunch = 1
  } else {
    bunch = cfg.panicEnter > 0 ? clamp(stress / cfg.panicEnter, 0, 1) : 0
    scatter = 0
  }
  const bunchedCohesion = 1 + (cfg.bunchCohesionMultiplier - 1) * bunch
  const bunchedSeparation = 1 + (cfg.bunchSeparationMultiplier - 1) * bunch
  out.cohesion = bunchedCohesion + (cfg.panicCohesionMultiplier - bunchedCohesion) * scatter
  out.separation = bunchedSeparation + (cfg.panicSeparationMultiplier - bunchedSeparation) * scatter
  out.content = 1 - bunch * (1 - scatter) * cfg.bunchContentDrop
}

/**
 * How close to the paddock edge counts as standing on it, in metres.
 *
 * Generous relative to a tick's travel (0.6 m) because the dog does not land exactly on the line —
 * it is clamped onto it, and then the next target is clamped to the same place.
 */
const WALL_EPS = 0.05

/**
 * Most relaxation passes to spend pushing overlapping bodies apart in one tick.
 *
 * The pass stops as soon as one produces no correction, which on a typical tick is the first — so
 * this is a ceiling on the worst case rather than a cost paid every tick. It needs to be more than
 * one because the corrections interact: separating a pair can push one of them into a third, and a
 * single sweep leaves that chain half-resolved.
 *
 * **Convergence is asymptotic, so this buys precision rather than correctness.** Measured on a flock
 * being driven against the paddock edge, the worst separation went 0.778 m at four passes, 0.794 at
 * eight and 0.7995 at sixteen, against a required 0.800. A hard guarantee is not available at any
 * number: a jam between a rigid boundary and another body has to give somewhere, and the boundary
 * wins.
 *
 * **Raised 8 → 16 when the flock got denser.** Bunching under pressure and driving from the point of
 * balance both pack a worked mob tighter, and at eight passes a dog leaning in at full press squeezed
 * a pair to 0.728 m — 7 cm of overlap, which is visible. Sixteen clears it. The ceiling costs
 * nothing on an ordinary tick because the sweep stops the moment one pass finds nothing to do, which
 * is almost always the first.
 */
const BODY_MAX_PASSES = 16

interface Sheep {
  pos: Vec2
  vel: Vec2
  acc: Vec2
  stress: number
  panicked: boolean
  penned: boolean
  /**
   * The way the animal is pointing, as a unit vector.
   *
   * Movement follows it: a sheep cannot strafe, which before this it could — velocity swung freely
   * to wherever the forces pointed, so a pressured mob slid sideways rather than turning and running.
   */
  heading: Vec2
  /** Idle wander heading. A unit-ish vector, random-walked — never derived from an angle (no trig). */
  wanderDir: Vec2
  /** Panic jitter heading. Same trig-free random walk as wanderDir, but used only under stress. */
  panicDir: Vec2
  /**
   * Ticks of run-on left from a fright. Set from the stress that caused it and counted down, so a
   * sheep keeps moving after its stress has decayed rather than stopping the moment it calms.
   */
  flightTicks: number
  /** Direction of the worst fright, remembered so a bolt runs AWAY rather than onward. */
  fleeDir: Vec2
  /**
   * Temperament, drawn once per sheep from the seeded RNG. Both are −1 … +1.
   *
   * `boldness` scales how readily this animal takes fright; `sociability` scales how strongly it
   * holds with the mob. A flock of identical agents behaves like one organism, which is legible but
   * lifeless — and it means the same push always produces the same result, so there is nothing to
   * read. Individuals give the flock a grain: one timid sheep sets the others off, one independent
   * one keeps drifting off the back and has to be collected.
   */
  boldness: number
  sociability: number
  /** Ticks remaining in the current graze/wander phase. */
  phaseTicks: number
  /** True while head-down grazing; false while taking idle steps. */
  grazing: boolean
}

export class Simulation {
  readonly level: Level
  readonly config: SimConfig
  readonly sheep: Sheep[] = []
  readonly dog: Vec2
  readonly dogTarget: Vec2
  /** World-frame velocity of the dog, from the step it actually took. Drives the charge response. */
  readonly dogVel = vec()

  tick = 0
  barks = 0
  won = false

  /**
   * Faults as they happen. Every one is an EVENT with a rising edge, never a per-tick tally — a
   * fault counted every tick would read "bit the stock ×47" for a single bad pass and make the
   * scorecard meaningless.
   */
  readonly faults: Faults = { rushes: 0, bites: 0, penEscapes: 0, crosses: 0 }
  /** Per-sheep latches, so one continuous event counts once. */
  private readonly biting: boolean[] = []
  private wasCalm = true
  private crossing = false
  private wasFullyPenned = false

  /**
   * The dog's standing order, or null when it is being driven directly.
   *
   * A command persists until another replaces it, which is what a standing order means in the
   * field and is the model P3 settled on. It is recomputed against the flock's CURRENT position
   * every tick rather than resolved once into a fixed point — told to hold the balance, the dog
   * keeps holding it as the mob moves.
   */
  order: CommandType | null = null

  /**
   * True once a flank has run its course and the dog has stopped at the balance point.
   *
   * **A flank is a trajectory, not endless circling.** Sent on "come by", a dog casts round and, on
   * reaching the head of the mob, stops and turns to face the stock — the *lift* — then transitions
   * from flanking to fetching. Orbiting forever was wrong, and it forced the player to countermand
   * at exactly the moment the dog would have stopped by itself.
   *
   * Re-issuing the same flank clears this and sends the dog round again, which is how a handler
   * overrides the instinct to stop and fetch. See PLAN.md § Playtest feedback P7.
   */
  lifted = false

  /**
   * Set when "look back" is given, cleared once the dog has switched mobs.
   *
   * The switch happens once, at the moment of the command, rather than every tick: re-deciding
   * continuously would make the dog oscillate between two mobs, which is the dithering the
   * commitment hysteresis exists to prevent.
   */
  private lookBackPending = false

  /**
   * Whether the dog is holding a mob it was deliberately sent back for.
   *
   * "Look back" switches which mob the dog is working — and the commitment rule that stops it
   * dithering between two mobs then switched it straight back, because that rule keeps the dog on
   * the LARGEST cluster and stragglers are by definition the smaller group. Measured: eight sheep
   * near the gate and three left at the far end, look back moved the dog for exactly one tick and
   * the run continued as though nothing had been said. The command did nothing in precisely the
   * situation it exists for.
   *
   * So a look-back commitment outranks size until the player says something else, which is what the
   * command means in the field: go back and fetch them, and stay with them until I tell you not to.
   */
  private lookBackLock = false

  /**
   * Repeats of "walk on" since it became the standing order. Each one leans the dog further in.
   *
   * Public so the command bar can show it: escalating pressure the player cannot see is escalating
   * pressure the player cannot manage, and stress is already the invisible half of the score.
   */
  pressSteps = 0

  /**
   * "Steady" — a pace modifier, not an order.
   *
   * A dog told to steady does not stop flanking; it flanks slower. So this rides alongside the
   * standing order rather than replacing it, and repeating the command turns it off again, which is
   * how a handler uses it.
   */
  steady = false

  /**
   * "Get back" — how much extra room the dog has been asked to give, in repeats.
   *
   * The opposite axis to `pressSteps`: walk-on repeats bring the dog in, get-back repeats send it
   * out. It widens every station rather than only the driving one, because "give them room" applies
   * to whatever the dog is doing.
   */
  backSteps = 0

  /** A flank may only end at balance once it has first got well away from it. */
  private flankArmed = false
  /** Signed side of the balance ray last tick, for detecting the sweep through it. */
  private prevCross = 0

  /**
   * Ticks the dog has spent gathering itself at a fence it is about to jump.
   *
   * The dog still crosses interior fences — a working dog jumps them, and that is what keeps fenced
   * layouts a constraint on the flock rather than a maze for the player — but it no longer glides
   * through as though they were not there. See PLAN.md § Playtest feedback P11.
   */
  private jumpCharge = 0

  /**
   * The fence a drive should work along, as a unit direction and a unit normal, or all zero when
   * none is near. Recomputed each tick an order is active; see `findDriveFence`.
   */
  /**
   * The radial the dog stood on when the current drive was ordered. Zero until one is given.
   * See CommandWorld.driveAnchorX for why this is captured once rather than tracked.
   */
  private driveAnchorX = 0
  private driveAnchorY = 0

  private fenceAx = 0
  private fenceAy = 0
  private fenceNx = 0
  private fenceNy = 0

  private barkTicksLeft = 0
  private barkCooldownLeft = 0
  private settledFor = 0
  private stressAccumulator = 0
  private readonly grid: SpatialGrid
  /**
   * Radius of the disc a flock of this size naturally packs into at `separationRadius` spacing.
   * Cohesion is zero inside it. Derived per-level because a 30-sheep flock settles into a much
   * wider disc than a 10-sheep one, and a fixed constant would either squeeze the big flock
   * forever or let the small one disperse.
   */
  readonly contentRadius: number
  /**
   * The simulation's own RNG stream, used for idle wander. Consumption order is deterministic
   * (sheep iterated by index, fixed calls per branch), which is what keeps replays reproducible.
   * Nothing outside the engine may draw from it.
   */
  private readonly rng: () => number

  // Scratch vectors — allocated once. Allocating inside the tick loop is both a GC problem and a
  // subtle source of nondeterministic timing.
  private readonly tmpA = vec()
  private readonly tmpB = vec()
  private readonly tmpC = vec()
  private readonly cohesion = vec()
  private readonly alignment = vec()
  private readonly separation = vec()
  private readonly steer = vec()
  /** Stable view of sheep positions handed to the grid — avoids an allocation every tick. */
  private readonly positions: Vec2[] = []
  /**
   * Which bodies the overlap pass actually moved this tick, so the fence and boundary re-checks run
   * only for them. Preallocated: the tick loop allocates nothing, and a `Set` would also put an
   * iteration order into the hot path.
   */
  private bodyMoved = new Uint8Array(0)
  /** Scratch for `flockWeights`, so the per-sheep call allocates nothing. */
  private readonly weights: FlockWeights = { cohesion: 1, separation: 1, content: 1 }
  /** Centroid of the sheep still to be worked, recomputed only while an order is active. */
  private readonly workingCentre = vec()
  /** Radial extent of the worked mob — used by the flank, which orbits rather than pushes. */
  private workingRadius = 0
  /** Which cluster id the dog is working, for `rearAlong`. */
  private workingCluster = -1

  /**
   * Position of the rearmost worked sheep along a unit direction, written into `out`.
   *
   * Bound once rather than created per tick. Commands offset from THIS rather than from the
   * centroid — see `CommandConfig` for why that distinction decides whether a command works at all.
   */
  private readonly rearAlong = (dx: number, dy: number, out: Vec2): void => {
    let best = -Infinity
    let idx = -1
    for (let i = 0; i < this.sheep.length; i++) {
      if (this.clusterOf[i] !== this.workingCluster) continue
      const proj =
        (this.sheep[i].pos.x - this.workingCentre.x) * dx +
        (this.sheep[i].pos.y - this.workingCentre.y) * dy
      if (proj > best) {
        best = proj
        idx = i
      }
    }
    if (idx < 0) copy(out, this.workingCentre)
    else copy(out, this.sheep[idx].pos)
  }
  /**
   * Index of a sheep in the mob the dog has committed to, or -1 before it has committed to any.
   *
   * A dog works a mob, it does not re-decide every tick. Without an anchor, two groups of 7 and 8
   * make the "largest" flip as sheep drift between them and the dog dithers in the middle — which
   * is exactly the confusion a split mob caused.
   *
   * -1 rather than 0 matters: seeding it to a real index fakes a commitment the dog has not made,
   * and the very first decision then follows sheep 0's group instead of the larger mob.
   */
  private workingAnchor = -1
  // Preallocated clustering scratch — the tick loop must not allocate.
  private readonly clusterOf: Int32Array
  private readonly clusterSize: Int32Array
  private readonly clusterStack: Int32Array
  /** Gate centre — what "behind" is measured against. Fixed for the level. */
  private readonly gateCentre: Vec2

  constructor(level: Level, seed: number, config: SimConfig = DEFAULT_CONFIG) {
    this.level = level
    this.config = config

    const rng = makeRng(seed)
    this.rng = rng
    for (let i = 0; i < level.flockSize; i++) {
      this.sheep.push({
        pos: vec(
          rngRange(rng, level.flockSpawn.x, level.flockSpawn.x + level.flockSpawn.w),
          rngRange(rng, level.flockSpawn.y, level.flockSpawn.y + level.flockSpawn.h),
        ),
        // Seeded initial drift, generated with the RNG rather than trig on a random angle.
        vel: vec(rngRange(rng, -1, 1), rngRange(rng, -1, 1)),
        acc: vec(),
        stress: 0,
        panicked: false,
        flightTicks: 0,
        fleeDir: vec(),
        // Two draws, in a fixed order, immediately after the other per-sheep draws — the RNG stream
        // shape is what keeps replays reproducible, so this position is not arbitrary.
        boldness: rngRange(rng, -1, 1),
        sociability: rngRange(rng, -1, 1),
        panicDir: vec(rng() * 2 - 1, rng() * 2 - 1),
        penned: false,
        wanderDir: vec(rngRange(rng, -1, 1), rngRange(rng, -1, 1)),
        // Seeded from the wander draw rather than from a draw of its own. A new `rng()` here would
        // shift the whole stream and re-roll every daily ever generated, for a value whose only job
        // is to point somewhere plausible on tick zero.
        heading: vec(),
        phaseTicks: rngInt(rng, config.grazeTicksMin, config.grazeTicksMax),
        grazing: true,
      })
    }

    for (let i = 0; i < this.sheep.length; i++) {
      const w = this.sheep[i].wanderDir
      const d = Math.sqrt(w.x * w.x + w.y * w.y)
      // Degenerate draws are possible and must not produce a zero heading, which has no direction to
      // turn away from.
      if (d > 1e-6) set(this.sheep[i].heading, w.x / d, w.y / d)
      else set(this.sheep[i].heading, 0, 1)
    }

    for (let i = 0; i < this.sheep.length; i++) this.positions.push(this.sheep[i].pos)
    this.bodyMoved = new Uint8Array(this.sheep.length)
    for (let i = 0; i < this.sheep.length; i++) this.biting.push(false)

    this.clusterOf = new Int32Array(this.sheep.length)
    this.clusterSize = new Int32Array(this.sheep.length)
    this.clusterStack = new Int32Array(this.sheep.length)

    this.gateCentre = vec(level.gate.x + level.gate.w / 2, level.gate.y + level.gate.h / 2)
    this.dog = vec(level.dogSpawn.x, level.dogSpawn.y)
    this.dogTarget = vec(level.dogSpawn.x, level.dogSpawn.y)

    // N sheep packed at spacing s fill a disc of radius ~ s·sqrt(N/3).
    this.contentRadius =
      config.separationRadius * Math.sqrt(Math.max(level.flockSize, 1) / 3) * config.contentmentFactor

    // Cell size must be >= the largest perception radius ANY sheep can have, or neighbour queries
    // miss entities. Perception now scales with stress (playtest P4), so the bound is the panicked
    // maximum, not the resting radius — and the grid sweeps a fixed 3×3 window, so getting this
    // wrong drops neighbours silently instead of erroring.
    const cellSize = Math.max(
      config.perceptionRadius * Math.max(config.panicPerceptionMultiplier, 1),
      config.perceptionRadius * Math.max(config.panicContagionMultiplier, 1),
      config.separationRadius,
      1,
    )
    this.grid = new SpatialGrid(level.world.w, level.world.h, cellSize)
  }

  /**
   * Neighbour-search cell size. Exposed because it is an invariant, not an implementation detail:
   * it must be >= the widest radius any sheep can perceive at, or queries drop neighbours silently.
   * There is a test asserting it against the panicked maxima.
   */
  get gridCellSize(): number {
    return this.grid.cellSize
  }

  get barking(): boolean {
    return this.barkTicksLeft > 0
  }

  /**
   * Centroid of the mob the dog is actually working, or null before any order has been given.
   *
   * Exposed because a commanding agent MUST reason about the same mob the dog is on. The bot
   * originally averaged every unpenned sheep, which is a different point entirely once the flock
   * splits — measured, its idea of the balance point swung from +0.36 to −1.00 and back roughly
   * once a second while the mob sat still and the dog orbited a sub-group perfectly calmly. The bot
   * then re-cast on every swing and never drove.
   */
  get mobCentre(): Vec2 | null {
    return this.workingCluster >= 0 ? this.workingCentre : null
  }

  /** False while the bark is on cooldown. Surfaced so the command bar can show it as spent. */
  get barkReady(): boolean {
    return this.barkCooldownLeft === 0
  }

  /** Apply the inputs recorded for this tick, then advance exactly one fixed step. */
  step(events: readonly InputEvent[] = []): void {
    let barkStarted = false
    for (let i = 0; i < events.length; i++) {
      const ev = events[i]
      // Clamp the target into the paddock. The pointer can sit in the letterboxed dead space
      // outside the world, and the dog would happily follow it out of bounds.
      //
      // Done here in the engine rather than in the input layer on purpose: this is also the path a
      // submitted input log takes during server-side replay verification, so a forged log cannot
      // park the dog outside the paddock either.
      const tx = clamp(ev.x, 0, this.level.world.w)
      const ty = clamp(ev.y, 0, this.level.world.h)
      if (ev.type === 'move') {
        // Direct control countermands a standing order. The two schemes drive the same target and
        // would otherwise fight for it every tick.
        this.order = null
        set(this.dogTarget, tx, ty)
      } else if (ev.type === 'bark' && this.barkCooldownLeft === 0) {
        // A dog can bark while it is working, so a bark leaves the standing order alone — and must
        // therefore not drag the dog to the event's coordinates either.
        if (this.order === null) set(this.dogTarget, tx, ty)
        this.barkTicksLeft = this.config.barkDurationTicks
        this.barkCooldownLeft = this.config.barkCooldownTicks
        this.barks++
        barkStarted = true
      } else if (ev.type === 'steady') {
        // A modifier: it changes pace and leaves the order alone. Repeating it cancels.
        this.steady = !this.steady

        /**
         * Steady also EASES OFF, and that is the point of it.
         *
         * "Walk on" accumulates — each repeat leans the dog further in — so without something that
         * winds it back the only way out of maximum pressure is to give a different command
         * entirely, which also stops the drive. Steady is the handler's way of saying *the same
         * thing, more gently*: it degrades the press rather than clearing it, because "steady" is a
         * moderation, not a stop. A different command still resets it outright, below.
         */
        this.pressSteps = Math.max(0, this.pressSteps - this.config.steadyPressRelief)
      } else if (ev.type === 'get-back') {
        // Also a modifier. Capped, so the dog cannot be sent into the next paddock.
        if (this.backSteps < this.config.getBackMax) this.backSteps++
        // And it clears the walk-on press, because these are not two settings — they are ONE axis
        // read from both ends. Walking on repeatedly brings the dog in; getting back repeatedly
        // sends it out. Walk-on already zeroes `backSteps`, and for a while this branch did not
        // zero `pressSteps`, which left the dog simultaneously told to stand off and to lean in.
        // The visible symptom was the walk-on button staying lit at full pressure after a get-back.
        this.pressSteps = 0
      } else if (isCommand(ev.type)) {
        // Repeating "walk on" leans the dog in rather than restarting it — the same gesture as
        // repeating a flank to override the lift. ANY OTHER COMMAND RESETS THE ESCALATION: pressure
        // accumulated for one job should not silently carry into the next one.
        // Clamped at the source. `commandTarget` already floors the *station* at `walkOnPressMax`,
        // so an uncapped counter changed nothing about the dog — but it is also what the command bar
        // renders the pressure from, and it ran away: eleven taps drove the button's fill past solid
        // and the label's contrast down to 2.5:1. State the UI reads must not carry a number the
        // engine ignores.
        // Walk-on's pace is 1-BASED: giving the command is pace 1 (the slow steady walk), and each
        // repeat is a notch up. Every other command drops it to 0, which is not a pace at all — it
        // is "no drive standing". Pace 0 is otherwise reachable only by easing off with `steady`.
        const next =
          ev.type === 'walk-on' ? (this.order === 'walk-on' ? this.pressSteps + 1 : 1) : 0
        this.pressSteps = next < this.config.walkOnPressMax ? next : this.config.walkOnPressMax
        // Asking the dog to come in and push is also asking it to stop standing off.
        if (ev.type === 'walk-on') {
          this.backSteps = 0
          // Anchor the drive's aim on the line the dog is standing on NOW, and only when the drive
          // starts. Repeats escalate pace; they must not re-anchor, or tapping the button would
          // ratchet the dog round the mob 30° at a time and hand the player a free outrun.
          if (this.order !== 'walk-on') this.captureDriveAnchor()
        }
        this.order = ev.type
        // Every command restarts the flank state machine. Re-issuing the SAME flank is therefore
        // an override: the dog that had stopped at balance casts off round the mob again, which is
        // exactly how a handler pushes a dog past its instinct to stop and fetch.
        this.lifted = false
        this.flankArmed = false
        this.prevCross = 0
        if (ev.type === 'look-back') this.lookBackPending = true
        // Any other order releases the dog from the mob it was sent back for. `steady` and
        // `get-back` never reach here — they are modifiers and leave the standing order alone, so a
        // player can ease or widen a dog that is away fetching stragglers.
        else this.lookBackLock = false
      }
    }

    if (this.order !== null) this.applyOrder(this.order)
    this.stepDog()
    // Spike after the dog moves, so the impulse is centred where the dog actually is this tick.
    // Applying it during event processing used the previous tick's position and made the spike
    // silently disagree with the repulsion forces in stepFlock.
    if (barkStarted) this.applyBarkSpike()
    this.stepWander()
    this.grid.rebuild(this.positions)
    this.stepFlock()
    this.separateBodies()
    this.stepStress()
    const pennedBefore = this.pennedCount
    this.evaluate()
    this.countFaults(pennedBefore)

    if (this.barkTicksLeft > 0) this.barkTicksLeft--
    if (this.barkCooldownLeft > 0) this.barkCooldownLeft--
    this.tick++
  }

  /**
   * How much presence the dog has this tick, scaling both its repulsion and the stress it causes.
   *
   * 1 normally, and `dropPressure` while dropped. This is what makes "drop" the release
   * valve it is in the field: the dog stays exactly where it is, close enough to hold the mob, but
   * stops being a threat — which is a quite different tool from backing away.
   */
  get dogPressure(): number {
    return this.order === 'drop' ? this.config.dropPressure : 1
  }

  /**
   * Point `dogTarget` where the standing order says it should go.
   *
   * The centroid is taken over the sheep still to be WORKED, not the whole flock: once some are in
   * the gate, including them drags the balance point into the pen and the dog stops driving the
   * stragglers. That is the mistake a handler would not make.
   */
  /**
   * Group the unpenned sheep into mobs, and commit the dog to one of them.
   *
   * **A centroid is the wrong answer for a split mob**, and this is the bug that produced it: with
   * the flock in two groups the mean position is the empty ground between them, so the dog worked a
   * point with no sheep at it — flanking around nothing and driving nothing. Every command is
   * defined against "the mob", so "the mob" has to be a real group of animals.
   *
   * Grouping is single-linkage at `perceptionRadius`: two sheep are in the same mob if they can
   * perceive each other, chained transitively. That is deliberately the same criterion the flock
   * itself uses for cohesion, so what the dog treats as one mob is what the sheep treat as one mob —
   * including a strung-out line, which chains into a single group exactly as it should.
   *
   * Returns false when there is nothing left to work.
   */
  private computeWorkingCentre(): boolean {
    const n = this.sheep.length
    const link = this.config.perceptionRadius
    const link2 = link * link

    // Flood fill in index order, so cluster ids and therefore every tie-break are deterministic.
    this.clusterOf.fill(-1)
    this.clusterSize.fill(0)
    let clusters = 0

    for (let i = 0; i < n; i++) {
      if (this.sheep[i].penned || this.clusterOf[i] !== -1) continue
      const id = clusters++
      let top = 0
      this.clusterStack[top++] = i
      this.clusterOf[i] = id
      this.clusterSize[id] = 1
      while (top > 0) {
        const a = this.clusterStack[--top]
        for (let b = 0; b < n; b++) {
          if (this.sheep[b].penned || this.clusterOf[b] !== -1) continue
          if (dist2(this.sheep[a].pos, this.sheep[b].pos) > link2) continue
          this.clusterOf[b] = id
          this.clusterSize[id]++
          this.clusterStack[top++] = b
        }
      }
    }

    if (clusters === 0) return false // every sheep is penned

    // Biggest mob, ties to the lowest sheep index so this never depends on iteration luck.
    let biggest = 0
    for (let id = 1; id < clusters; id++) {
      if (this.clusterSize[id] > this.clusterSize[biggest]) biggest = id
    }

    // Stay with the mob the dog is already on unless it is gone or clearly the lesser one. The
    // hysteresis is the point: re-deciding every tick makes 7-against-8 flip back and forth and the
    // dog dithers between two groups instead of working either.
    const anchor = this.workingAnchor
    const anchored =
      anchor >= 0 && anchor < n && !this.sheep[anchor].penned ? this.clusterOf[anchor] : -1
    let working = biggest
    if (anchored >= 0 && (this.lookBackLock || this.clusterSize[anchored] * 2 >= this.clusterSize[biggest])) {
      // A mob the dog was sent back for is kept whatever its size — that is the whole point of
      // having been sent. Otherwise the dog stays with its mob unless that mob has shrunk to less
      // than half the largest, which is the hysteresis that stops it dithering between two.
      working = anchored
    } else if (this.lookBackLock) {
      // The fetched mob is gone (penned, or its last sheep joined another). Nothing to hold on to.
      this.lookBackLock = false
    }

    /**
     * "Look back" — turn off this mob and go back for the stock left behind.
     *
     * This is the player's manual override of the dog's commitment. The commitment exists so the dog
     * does not dither between two mobs, and the cost of that is a dog which will happily work eight
     * sheep while three sit forgotten on the far side of the paddock. A handler's answer to exactly
     * that is to send the dog back, so this picks the largest mob that is NOT the one being worked.
     *
     * With the flock all together there is nothing behind, and the command is a no-op that resolves
     * to holding balance — which is also what a dog does when sent back and finding nothing.
     */
    if (this.lookBackPending) {
      this.lookBackPending = false

      /**
       * "Look back" means a specific thing in space: *further from the goal, behind you, there are
       * stragglers*. So the mob it picks is the one BEHIND — further from the gate than the one the
       * dog is on — and only failing that the largest of whatever else is out there.
       *
       * Picking the largest other mob was the first version and is subtly wrong: sent back, a dog
       * that turned toward stock nearer the gate than the ones it was already working would be
       * going forward, not back.
       */
      let workingGateD2 = Infinity
      let cx0 = 0
      let cy0 = 0
      let n0 = 0
      for (let i = 0; i < n; i++) {
        if (this.clusterOf[i] !== working) continue
        cx0 += this.sheep[i].pos.x
        cy0 += this.sheep[i].pos.y
        n0++
      }
      if (n0 > 0) {
        workingGateD2 = dist2(vec(cx0 / n0, cy0 / n0), this.gateCentre)
      }

      let behind = -1
      let other = -1
      for (let id = 0; id < clusters; id++) {
        if (id === working) continue
        let cx = 0
        let cy = 0
        let cn = 0
        for (let i = 0; i < n; i++) {
          if (this.clusterOf[i] !== id) continue
          cx += this.sheep[i].pos.x
          cy += this.sheep[i].pos.y
          cn++
        }
        if (cn === 0) continue
        const d2 = dist2(vec(cx / cn, cy / cn), this.gateCentre)
        if (d2 > workingGateD2) {
          if (behind < 0 || this.clusterSize[id] > this.clusterSize[behind]) behind = id
        }
        if (other < 0 || this.clusterSize[id] > this.clusterSize[other]) other = id
      }
      const pick = behind >= 0 ? behind : other
      if (pick >= 0) {
        working = pick
        this.lookBackLock = true
      }
    }

    let cx = 0
    let cy = 0
    let count = 0
    let first = -1
    for (let i = 0; i < n; i++) {
      if (this.clusterOf[i] !== working) continue
      if (first < 0) first = i
      cx += this.sheep[i].pos.x
      cy += this.sheep[i].pos.y
      count++
    }
    if (count === 0) return false

    this.workingAnchor = first
    set(this.workingCentre, cx / count, cy / count)

    this.workingCluster = working

    // Radial extent, for the flank only. Every pushing command offsets from the rear SHEEP instead
    // (see `rearAlong`), because a mob is not a disc and a centroid-plus-extent station leaves the
    // dog touching nothing whenever the flock is elongated across its approach.
    let far2 = 0
    for (let i = 0; i < n; i++) {
      if (this.clusterOf[i] !== working) continue
      const d2 = dist2(this.sheep[i].pos, this.workingCentre)
      if (d2 > far2) far2 = d2
    }
    this.workingRadius = Math.sqrt(far2)
    return true
  }

  /**
   * Decide whether the flank has arrived at the balance point.
   *
   * Two ways in, because either alone has a hole. Sweeping *through* the balance ray is detected by
   * the sign of `cross` flipping, which cannot be stepped over however fast the dog is covering
   * angle — and a dog flanking at a small radius covers a lot of it per tick. But a dog that
   * converges on balance without ever crossing would never trip that, so proximity closes the gap.
   *
   * Arming first is what makes re-issuing a flank an override rather than a no-op: the dog must get
   * well away from balance before it is allowed to stop there.
   */
  private updateLift(world: CommandWorld): void {
    const { aligned, cross } = balanceAlignment(world)

    if (!this.flankArmed) {
      if (aligned < LIFT_ARM_COS) this.flankArmed = true
      this.prevCross = cross
      return
    }

    const swept = this.prevCross !== 0 && cross !== 0 && this.prevCross * cross < 0
    // Only the balance ray counts, not the ray directly opposite it, which shares a sign change.
    if ((swept && aligned > 0) || aligned > LIFT_ENTER_COS) this.lifted = true
    this.prevCross = cross
  }

  private applyOrder(order: CommandType): void {
    if (!this.computeWorkingCentre()) return
    this.findDriveFence()

    const world = {
      dog: this.dog,
      flock: this.workingCentre,
      gate: this.gateCentre,
      rearAlong: this.rearAlong,
      mobRadius: this.workingRadius,
      dogRadius: this.config.dogRadius,
      // A few ticks of travel, so the heading is stable rather than recomputed into a jitter.
      lookahead: this.config.dogSpeed * DT * 4,
      pressSteps: this.pressSteps,
      backSteps: this.backSteps,
      driveAnchorX: this.driveAnchorX,
      driveAnchorY: this.driveAnchorY,
      fenceAx: this.fenceAx,
      fenceAy: this.fenceAy,
      fenceNx: this.fenceNx,
      fenceNy: this.fenceNy,
    }


    let effective: CommandType = order === 'look-back' ? 'get-behind' : order

    // A flank runs until the dog reaches the head of the mob, then stops and faces the stock.
    if (order === 'come-by' || order === 'away') {
      if (!this.lifted) this.updateLift(world)
      // The lift is a stop AT balance, which is the same station "get behind" holds.
      if (this.lifted) effective = 'get-behind'
    }

    /**
     * "Get behind" CASTS. It does not walk to the balance point in a straight line.
     *
     * Aiming straight at balance is how a dog ends up crossing its own stock: from the gate side the
     * shortest path to "behind them" goes through the middle of the mob, and measured, it did —
     * 0.3 m from the flock's centre, threading between animals. That is the one thing a working dog
     * is trained out of. A real dog casts round the outside and arrives at the head of the mob,
     * which is exactly the flank-plus-lift this engine already knows how to do.
     *
     * So the command resolves to a flank in whichever direction turns toward balance, and the lift
     * ends it in the right place. The player presses one button and gets an outrun.
     */
    if ((order === 'get-behind' || order === 'look-back') && !this.lifted) {
      const { aligned, cross } = balanceAlignment(world)
      if (aligned < LIFT_ENTER_COS) {
        this.updateLift(world)
        // `cross` is the side the balance point lies on, so its sign picks the shorter way round.
        effective = cross > 0 ? 'come-by' : 'away'
      } else {
        this.lifted = true
      }
    }

    commandTarget(
      effective,
      world,
      this.config,
      this.dogTarget,
    )

    // A dog run out of room by the paddock edge turns and works along it. Must happen BEFORE the
    // clamp: clamping a point that lies outside the world throws away the direction that produced
    // it, which is what used to leave the dog standing on the fence line.
    this.slideAlongWalls(effective)

    // Same clamp the direct-control path gets: a standing order must not walk the dog out of the
    // paddock either, and this path is equally reachable from a submitted input log.
    this.dogTarget.x = clamp(this.dogTarget.x, 0, this.level.world.w)
    this.dogTarget.y = clamp(this.dogTarget.y, 0, this.level.world.h)
  }

  /**
   * Turn a target that lies outside the paddock into one along the wall, so an order aimed off the
   * edge of the world makes the dog run the fence line rather than stand on it.
   *
   * **This closes a total deadlock, not a rough edge.** Clamping the target point (the only thing
   * that used to happen) is harmless while the dog is in open ground, because the clamp merely
   * shortens a step. Standing ON the boundary it is fatal: the target clamps to within a few
   * centimetres of the dog's own position, `stepDog` sees a distance under one step and snaps
   * straight to it, and the dog is stationary while carrying out a standing order it believes it is
   * executing.
   *
   * Measured on open-paddock #3 — a flank round a mob wedged in the top corner. The flank's outward
   * radial and its tangent very nearly cancelled in x while both pushed −y into the wall, giving a
   * heading of (0.014, −0.973): the target clamped 3 cm away, the dog crept 0.01 m in 20 ticks, and
   * the nearest sheep sat at exactly `dogRadius`, where the repulsion falloff reaches zero. Neither
   * animal had any reason to move, and neither did, for 250 s of a 300 s run. Four of the seeds the
   * per-template gate was failing were this, on three different templates and both long walls.
   *
   * **A pinned flank runs the wall in the direction it was told to flank**, and that is a rule
   * rather than a preference. The blocked heading's leftover sideways component is dominated by the
   * radial correction — "get out to your working distance" — which is exactly the thing a wall makes
   * impossible, and against the bottom fence it points the opposite way to the cast. Trusting it
   * cost chicane #34: the dog alternated between the residual and the tangent on either side of a
   * threshold and oscillated over 1.2 m of wall for 200 s, never passing the mob and so never
   * sweeping through the balance ray that ends a flank. Committing to the tangent lets the cast
   * finish, which is the whole point of being sent round.
   *
   * Pushing orders have no tangent to fall back on and keep the residual, which is honest: a dog
   * told to walk on through a fence has nowhere to go, and standing there says so.
   */
  private slideAlongWalls(effective: CommandType): void {
    const world = this.level.world
    let hx = this.dogTarget.x - this.dog.x
    let hy = this.dogTarget.y - this.dog.y
    const mag = Math.sqrt(hx * hx + hy * hy)
    if (mag < 1e-6) return

    const blockedX =
      (this.dog.x <= WALL_EPS && hx < 0) || (this.dog.x >= world.w - WALL_EPS && hx > 0)
    const blockedY =
      (this.dog.y <= WALL_EPS && hy < 0) || (this.dog.y >= world.h - WALL_EPS && hy > 0)
    if (!blockedX && !blockedY) return

    let along = 0

    // A flank keeps flanking: its tangent around the mob is the direction the handler asked for,
    // and it is also the one that can still sweep the dog through the balance point.
    if (effective === 'come-by' || effective === 'away') {
      const rx = this.dog.x - this.workingCentre.x
      const ry = this.dog.y - this.workingCentre.y
      const rd = Math.sqrt(rx * rx + ry * ry)
      if (rd > 1e-6) {
        const sign = effective === 'come-by' ? 1 : -1
        const tx = blockedX ? 0 : (-ry / rd) * sign
        const ty = blockedY ? 0 : (rx / rd) * sign
        const td = Math.sqrt(tx * tx + ty * ty)
        if (td > 1e-6) {
          hx = tx
          hy = ty
          along = td
        }
      }
    }

    // Anything else — and a flank whose tangent runs into the wall too — slides on whatever the
    // ordered heading has left once the blocked component is removed.
    if (along === 0) {
      if (blockedX) hx = 0
      if (blockedY) hy = 0
      along = Math.sqrt(hx * hx + hy * hy)
      if (along < 1e-6) return // genuinely nowhere to go along this wall; the clamp holds the dog
    }

    // Rescale to the heading's original length, so sliding runs at working pace rather than at
    // whatever fraction happened to survive.
    const k = mag / along
    this.dogTarget.x = this.dog.x + hx * k
    this.dogTarget.y = this.dog.y + hy * k
  }

  /**
   * Find the fence nearest the worked mob, for a drive to work along rather than into.
   *
   * Written into fields rather than returned, because this runs every tick a standing order is
   * active and the engine allocates nothing in the tick loop. Zeroed when nothing is near, which is
   * how `commandTarget` tells there is no fence to follow.
   *
   * The perimeter is made of ordinary fence segments, so this covers the paddock walls too — which
   * is the case that matters most, since a mob driven into a wall is a mob that jams in a corner.
   */
  /** Remember which side of the mob the dog was on as a drive begins. */
  private captureDriveAnchor(): void {
    this.driveAnchorX = 0
    this.driveAnchorY = 0
    if (!this.computeWorkingCentre()) return
    const dx = this.dog.x - this.workingCentre.x
    const dy = this.dog.y - this.workingCentre.y
    const d = Math.sqrt(dx * dx + dy * dy)
    if (d < 1e-6) return
    this.driveAnchorX = dx / d
    this.driveAnchorY = dy / d
  }

  private findDriveFence(): void {
    this.fenceAx = 0
    this.fenceAy = 0
    this.fenceNx = 0
    this.fenceNy = 0

    if (this.config.walkOnFenceRadius <= 0) return // the behaviour is off, not merely short-sighted

    // From the mob's EDGE, so the rule means the same thing to a tight bunch as to a strung-out
    // line — the same reasoning as every station in commands.ts.
    const reach = this.config.walkOnFenceRadius + this.workingRadius
    const reach2 = reach * reach

    let bestD2 = reach2
    let best = -1
    for (let i = 0; i < this.level.fences.length; i++) {
      const f = this.level.fences[i]
      const d2 = closestPointOnSegment(this.workingCentre, f.a, f.b, this.tmpA)
      if (d2 < bestD2) {
        bestD2 = d2
        best = i
      }
    }
    if (best < 0) return

    const f = this.level.fences[best]
    let ax = f.b.x - f.a.x
    let ay = f.b.y - f.a.y
    const ad = Math.sqrt(ax * ax + ay * ay)
    if (ad < 1e-6) return
    ax /= ad
    ay /= ad

    // Orient ALONG the fence so that following it is progress: whichever end is gateward.
    if (ax * (this.gateCentre.x - this.workingCentre.x) + ay * (this.gateCentre.y - this.workingCentre.y) < 0) {
      ax = -ax
      ay = -ay
    }

    // And the normal pointing from the fence back toward the mob, which is what says whether a push
    // is heading into it. Recomputed from the closest point rather than from the segment, so it is
    // correct at the ends as well as along the middle.
    closestPointOnSegment(this.workingCentre, f.a, f.b, this.tmpA)
    let nx = this.workingCentre.x - this.tmpA.x
    let ny = this.workingCentre.y - this.tmpA.y
    const nd = Math.sqrt(nx * nx + ny * ny)
    if (nd < 1e-6) return // the mob is standing on the line; no side to work from
    nx /= nd
    ny /= nd

    this.fenceAx = ax
    this.fenceAy = ay
    this.fenceNx = nx
    this.fenceNy = ny
  }

  /** Would moving from `from` to `to` cross an interior fence? */
  private crossesFence(from: Vec2, to: Vec2): boolean {
    for (let i = 0; i < this.level.fences.length; i++) {
      const f = this.level.fences[i]
      if (segmentsCross(from, to, f.a, f.b)) return true
    }
    return false
  }

  /**
   * Steer a heading around individual sheep, so the dog skirts the mob instead of walking through it.
   *
   * **Per sheep, not around the mob as a whole**, and that is the second design here rather than the
   * first. The first drew a keep-out disc around the worked mob and aimed at its tangent — elegant,
   * and wrong, because a dog *works at the mob's edge*: measured, the dog sat 9.6 m from the worked
   * cluster's centre against a 12 m disc, so it began virtually every order already inside its own
   * keep-out zone and the avoidance never engaged once in 51 ticks. The rule was never "stay away
   * from the flock" — the dog is supposed to be close. It is "do not run through the animals".
   *
   * So: any sheep the dog is heading at and about to clip has its inward component removed from the
   * heading, leaving the tangential part. The dog slides around it rather than into it, which is
   * both what a dog does and what makes a flank read as a cast rather than a shove.
   *
   * `walk-on` is exempt — closing is its entire purpose.
   */
  private avoidSheep(hx: number, hy: number, out: Vec2): void {
    set(out, hx, hy)
    if (this.order === null || this.order === 'walk-on') return

    const clear = this.config.sheepClearance
    /**
     * How far ahead to look for something to go round.
     *
     * One step plus the clearance was not enough, and the measurement says why: at 0.6 m of travel
     * per tick that is about four ticks of warning, so the dog began turning when it was already
     * among the sheep and still passed within 1.6 m of one. A turn has to start before the animal is
     * in reach, not as it arrives.
     */
    const look = this.config.dogSpeed * DT * this.config.avoidLookTicks + clear

    for (let i = 0; i < this.sheep.length; i++) {
      const s = this.sheep[i]
      let nx = s.pos.x - this.dog.x
      let ny = s.pos.y - this.dog.y
      const d = Math.sqrt(nx * nx + ny * ny)
      if (d > look || d < 1e-6) continue
      nx /= d
      ny /= d
      const inward = out.x * nx + out.y * ny
      if (inward <= 0) continue // already heading away from this one

      // Drop the component that would take the dog into the sheep, and add a little outward bias
      // when it is genuinely close, so a dog boxed in by the mob still works its way clear.
      const push = d < clear ? (clear - d) / clear : 0
      out.x -= nx * (inward + push)
      out.y -= ny * (inward + push)
    }

    const len = Math.sqrt(out.x * out.x + out.y * out.y)
    if (len > 1e-6) {
      out.x /= len
      out.y /= len
    } else {
      set(out, hx, hy) // fully boxed in; keep the original heading rather than stopping dead
    }
  }

  private stepDog(): void {
    const prevX = this.dog.x
    const prevY = this.dog.y

    // The dog moves at a capped speed toward the pointer — it never teleports, so a careless
    // player can be outmanoeuvred by the flock.
    sub(this.tmpA, this.dogTarget, this.dog)
    const d = len(this.tmpA)
    // Pace: a dog told to walk on gets on with it, slightly at first and more with each repeat.
    // Multiplies with `steady` rather than replacing it, so a steady walk-on is still the slower of
    // the two at the same pace.
    const pace =
      this.order === 'walk-on' ? 1 + this.pressSteps * this.config.walkOnPaceStep : 1
    const maxStep =
      this.config.dogSpeed * DT * (this.steady ? this.config.steadyFactor : 1) * pace
    const next = this.tmpC
    if (d <= maxStep || d === 0) {
      copy(next, this.dogTarget)
    } else {
      // Heading first, then steered around any sheep in the way, then stepped.
      this.avoidSheep(this.tmpA.x / d, this.tmpA.y / d, this.tmpB)
      copy(next, this.dog)
      addScaled(next, this.tmpB, maxStep)
    }

    // Jumping a fence costs time. The dog gathers itself at the line for `dogJumpTicks`, then goes
    // over — so a fence between the dog and where it needs to be is a real cost to the player,
    // while still never being a wall. Counting up and clearing on a non-crossing step makes this
    // obviously terminating: the dog cannot get stuck re-charging the same jump forever.
    // Once a jump is started the dog SEES IT THROUGH, rather than the charge resetting on any step
    // that happens not to cross — a dog working along a fence alternates crossing and non-crossing
    // steps, and would otherwise never accumulate enough to get over.
    //
    // Honestly: this was written to fix a 300 s stall on chicane and dogleg and did NOT fix it. The
    // stall was simply the size of the jump cost, found by sweeping it. Kept because "a jump
    // completes" is the more defensible model, not because it earned its place by fixing anything.
    if (this.jumpCharge > 0) {
      this.jumpCharge++
      if (this.jumpCharge <= this.config.dogJumpTicks) {
        set(this.dogVel, 0, 0) // gathering at the line: not moving, and therefore not charging
        return
      }
      this.jumpCharge = 0
    } else if (this.crossesFence(this.dog, next)) {
      this.jumpCharge = 1
      set(this.dogVel, 0, 0)
      return
    }

    copy(this.dog, next)

    // Belt and braces alongside the target clamp above — the dog can never leave the paddock.
    // Note it deliberately does NOT collide with interior fences: a working dog jumps them, and
    // that is what makes fenced layouts a constraint on the flock rather than on the player.
    this.dog.x = clamp(this.dog.x, 0, this.level.world.w)
    this.dog.y = clamp(this.dog.y, 0, this.level.world.h)

    // Derived from the step actually taken, so it accounts for every clamp above rather than for
    // what the dog intended. Used by the stress model to tell a charge from a presence.
    set(this.dogVel, (this.dog.x - prevX) / DT, (this.dog.y - prevY) / DT)
  }

  private applyBarkSpike(): void {
    const radius = this.config.dogRadius * this.config.barkRadiusMultiplier
    const r2 = radius * radius
    for (let i = 0; i < this.sheep.length; i++) {
      const s = this.sheep[i]
      if (dist2(s.pos, this.dog) <= r2) {
        s.stress = clamp(s.stress + this.config.stressBarkSpike, 0, 1)
      }
    }
  }

  /**
   * Idle behaviour: sheep alternate between head-down grazing and a few wandering steps.
   *
   * Kept entirely separate from the flocking rules so it cannot interfere with the contentment
   * dead zones (see PLAN.md § Open problem 1) — wander is a small additive force, not a change to
   * whether cohesion is satisfied.
   *
   * Deliberately trig-free: the heading is random-walked as a vector and renormalised, never
   * generated from an angle, because Math.sin/cos are not correctly rounded across JS engines and
   * would break cross-engine replay.
   */
  private stepWander(): void {
    const cfg = this.config
    for (let i = 0; i < this.sheep.length; i++) {
      const s = this.sheep[i]

      // Panic jitter heading drifts for every sheep every tick, regardless of graze phase — a
      // frightened sheep is not on the graze cycle at all, so it cannot share wanderDir's state.
      s.panicDir.x += (this.rng() * 2 - 1) * cfg.wanderJitter
      s.panicDir.y += (this.rng() * 2 - 1) * cfg.wanderJitter
      normalize(s.panicDir, s.panicDir)

      s.phaseTicks--
      if (s.phaseTicks <= 0) {
        s.grazing = !s.grazing
        s.phaseTicks = s.grazing
          ? rngInt(this.rng, cfg.grazeTicksMin, cfg.grazeTicksMax)
          : rngInt(this.rng, cfg.wanderTicksMin, cfg.wanderTicksMax)
        if (!s.grazing) {
          // Pick a fresh heading when starting to move.
          set(s.wanderDir, this.rng() * 2 - 1, this.rng() * 2 - 1)
        }
      }

      if (s.grazing) continue

      // Random-walk the heading so the path curves rather than being a straight line.
      s.wanderDir.x += (this.rng() * 2 - 1) * cfg.wanderJitter
      s.wanderDir.y += (this.rng() * 2 - 1) * cfg.wanderJitter
      normalize(s.wanderDir, s.wanderDir)
    }
  }

  private stepFlock(): void {
    /**
     * The most a sheep may turn this tick, as a cosine/sine pair.
     *
     * `turnRate` is radians per WORLD second, so the per-tick angle is `turnRate × DT` and survives
     * SIM_SPEED moving. The pair is built from the Taylor series rather than `Math.cos`/`Math.sin`,
     * which are not correctly rounded and genuinely differ between JavaScript engines — the sim path
     * may not contain one. At 4 rad/s the angle is about 0.107 rad, where the series is good to
     * better than a part in a million, and the result is renormalised anyway.
     */
    const phi = this.config.turnRate * DT
    const phi2 = phi * phi
    const turnCos = 1 - phi2 / 2 + (phi2 * phi2) / 24
    const turnSin = phi - (phi2 * phi) / 6 + (phi2 * phi2 * phi) / 120

    const cfg = this.config
    const separation2 = cfg.separationRadius * cfg.separationRadius

    const dogRadius = cfg.dogRadius * (this.barking ? cfg.barkRadiusMultiplier : 1)
    // Presence scales the force but deliberately NOT the radius: a lying dog is still seen from
    // just as far away, it is simply not pushing. Shrinking the radius instead would let the flock
    // drift in and then be shoved when the dog got up, which is the opposite of the command.
    const dogForce = cfg.dogForce * (this.barking ? cfg.barkForceMultiplier : 1) * this.dogPressure

    for (let i = 0; i < this.sheep.length; i++) {
      const self = this.sheep[i]
      set(self.acc, 0, 0)
      set(this.cohesion, 0, 0)
      set(this.alignment, 0, 0)
      set(this.separation, 0, 0)
      let flockCount = 0
      let separationCount = 0

      // Perception widens with fright (playtest P4): a panicking sheep is aware of far more of the
      // mob than a grazing one. Scaled continuously with stress rather than switching on `panicked`,
      // which would pop the neighbourhood in and out at the hysteresis boundary. The grid's cell
      // size is built from the value this can reach at stress 1 — see the constructor.
      const perception =
        cfg.perceptionRadius * (1 + (cfg.panicPerceptionMultiplier - 1) * self.stress)
      const perception2 = perception * perception

      this.grid.forEachNeighbour(self.pos, (j) => {
        if (j === i) return
        const other = this.sheep[j]
        const d2 = dist2(self.pos, other.pos)
        if (d2 > perception2) return

        this.cohesion.x += other.pos.x
        this.cohesion.y += other.pos.y
        this.alignment.x += other.vel.x
        this.alignment.y += other.vel.y
        flockCount++

        if (d2 < separation2 && d2 > 0) {
          // Push proportional to overlap depth: 0 at exactly separationRadius, 1 when coincident.
          // An inverse-square sum was used first, but its magnitude is unbounded and had to be
          // renormalised to maxForce, which is precisely what stopped the force ever reaching zero.
          const d = Math.sqrt(d2)
          const depth = 1 - d / cfg.separationRadius
          const inv = depth / d
          this.separation.x += (self.pos.x - other.pos.x) * inv
          this.separation.y += (self.pos.y - other.pos.y) * inv
          separationCount++
        }
      })

      /**
       * Pressure BUNCHES a flock, and only fright scatters it.
       *
       * Two regimes rather than a switch on `panicked`. Below the panic threshold, rising stress
       * pulls the mob together — cohesion up, personal space down, and the contentment radius in, so
       * the disc they are content to sit in genuinely shrinks. Past it, the same numbers hand over
       * to the panic values and the mob comes apart.
       *
       * The old model had only the second half, so *any* pressure made sheep spread and stop holding
       * together. That is backwards about the animal — sheep bunch under threat, which is why
       * herding works at all — and it made the dog an instrument that could only ever scatter its
       * own stock. The two branches meet exactly at `panicEnter`, so nothing jumps as a sheep
       * crosses it, and `panicked` keeps its hysteresis band so it cannot flicker there either.
       */
      flockWeights(self.stress, self.panicked, cfg, this.weights)
      const cohesionMul = this.weights.cohesion
      const separationMul = this.weights.separation
      const contentRadius = this.contentRadius * this.weights.content

      // Temperament: a herd-bound sheep holds with the mob harder than an independent one.
      const cohesionW =
        cfg.cohesionWeight * cohesionMul * (1 + self.sociability * cfg.traitSociability)
      const separationW = cfg.separationWeight * separationMul
      const maxSpeed = cfg.maxSpeed * (self.panicked ? cfg.panicSpeedMultiplier : 1)

      if (flockCount > 0) {
        const inv = 1 / flockCount

        // COHESION — zero inside the contentment radius, ramping in beyond it. A sheep already
        // snug with its neighbours must feel nothing, or damping has a force to fight forever.
        set(this.tmpA, this.cohesion.x * inv - self.pos.x, this.cohesion.y * inv - self.pos.y)
        const centroidDist = len(this.tmpA)
        if (centroidDist > contentRadius) {
          const strength = clamp((centroidDist - contentRadius) / cfg.cohesionRamp, 0, 1)
          addScaled(
            self.acc,
            normalize(this.tmpB, this.tmpA),
            cfg.maxForce * cohesionW * strength,
          )
        }

        // ALIGNMENT — zero while already matching the neighbourhood closely enough.
        set(this.tmpA, this.alignment.x * inv - self.vel.x, this.alignment.y * inv - self.vel.y)
        const velMismatch = len(this.tmpA)
        if (velMismatch > cfg.alignTolerance) {
          const strength = clamp((velMismatch - cfg.alignTolerance) / cfg.alignRamp, 0, 1)
          addScaled(
            self.acc,
            normalize(this.tmpB, this.tmpA),
            cfg.maxForce * cfg.alignmentWeight * strength,
          )
        }
      }

      if (separationCount > 0) {
        // Magnitude is already bounded in [0,1] per neighbour by the overlap-depth weighting, so
        // it is scaled directly rather than renormalised — which keeps it able to reach zero.
        const strength = clamp(len(this.separation) / separationCount, 0, 1)
        if (strength > 0) {
          addScaled(
            self.acc,
            normalize(this.tmpB, this.separation),
            cfg.maxForce * separationW * strength,
          )
        }
      }

      /**
       * Dog pressure, read against the animal's POINT OF BALANCE.
       *
       * Stock handling's oldest idea: the balance point is at the shoulder, and pressure behind it
       * drives the animal on while pressure ahead of it checks or turns it. Repulsion used to be
       * purely radial, so the dog's distance mattered and its angle did not — a mob pushed from
       * astern fanned outward, every sheep radiating away from the dog instead of the body of them
       * travelling down one line.
       *
       * So the push is blended from "away from the dog" toward "on down your own line" by how far
       * astern the dog is. Directly behind, the two nearly agree anyway; it is at the quarters that
       * this earns its place, where a dog behind the shoulder now sends a sheep forward rather than
       * sideways-away. Ahead of the balance point the blend is zero and the force stays radial,
       * which is what backs an animal off.
       *
       * The TURNING half needed nothing new. Since sheep gained a facing they cannot strafe, so a
       * push at the shoulder already rotates the animal instead of sliding it — that fell out of the
       * previous pass rather than having to be built here.
       *
       * The flight zone itself varies with temperament: a bold animal lets the dog come closer
       * before it moves.
       */
      const zone = dogRadius * (1 - self.boldness * cfg.flightZoneSpread)
      const dogD2 = dist2(self.pos, this.dog)
      if (dogD2 < zone * zone && dogD2 > 0) {
        const d = Math.sqrt(dogD2)
        const falloff = 1 - d / zone
        // Unit vector from the dog to the sheep — the direction "away".
        const ax = (self.pos.x - this.dog.x) / d
        const ay = (self.pos.y - this.dog.y) / d
        // +1 when the dog is directly astern, −1 when it is directly in front of the nose.
        const astern = ax * self.heading.x + ay * self.heading.y
        const drive = astern > 0 ? astern * cfg.balanceBias : 0
        const fx = ax * (1 - drive) + self.heading.x * drive
        const fy = ay * (1 - drive) + self.heading.y * drive
        const fd = Math.sqrt(fx * fx + fy * fy)
        if (fd > 1e-9) {
          set(this.tmpB, fx / fd, fy / fd)
          addScaled(self.acc, this.tmpB, dogForce * falloff)
        }
      }

      // Idle wander — only while calm and actually stepping. A stressed sheep has its head up and
      // is not grazing, so this fades out exactly as pressure comes on.
      if (!self.grazing && self.stress < cfg.panicExit) {
        const calm = 1 - self.stress / cfg.panicExit
        addScaled(self.acc, self.wanderDir, cfg.wanderForce * calm)
      }

      // Panic jitter — the mirror image of wander, fading IN as stress rises. Without it a
      // frightened sheep runs on pure flocking forces and is the most predictable thing in the
      // paddock; a spooked animal should be the least. Scaled past the threshold so calm sheep are
      // untouched and there is no discontinuity at the boundary.
      if (self.stress > cfg.panicJitterThreshold) {
        const fright = (self.stress - cfg.panicJitterThreshold) / (1 - cfg.panicJitterThreshold)
        addScaled(self.acc, self.panicDir, cfg.panicJitterForce * fright)
      }

      /**
       * The bolt.
       *
       * Suppressing damping made a frightened sheep *coast*, and coasting is not fleeing: a dog
       * charging through the middle of a settled mob left it moving at 1.27 m/s against a panicked
       * cap of 16, because dog repulsion is the only outward force and it is gone the instant the
       * dog is more than `dogRadius` away. Exposure is measured in ticks; the reaction has to
       * outlast it.
       *
       * Fades with the flight timer, and is silent for any sheep not in flight — which is every
       * calm sheep, so settling is untouched.
       */
      if (self.flightTicks > 0 && cfg.flightTicksMax > 0) {
        addScaled(self.acc, self.fleeDir, cfg.fleeForce * (self.flightTicks / cfg.flightTicksMax))
      }

      this.applyFenceAvoidance(self)

      limit(self.acc, self.acc, cfg.maxForce)
      addScaled(self.vel, self.acc, DT)
      limit(self.vel, self.vel, maxSpeed)

      // Grazing: calm sheep bleed off speed and come to rest. Panicked sheep do not — which is why
      // a stressed flock cannot be settled into the pen until it calms down.
      //
      // Flight run-on: a sheep that has been made to run keeps its feet for a beat after the fright
      // fades, so damping sees whichever is higher — its current stress, or what is left of the
      // flight it was pushed into.
      const flight = cfg.flightTicksMax > 0 ? self.flightTicks / cfg.flightTicksMax : 0
      const damping = cfg.grazeDamping * (1 - (flight > self.stress ? flight : self.stress))
      if (damping > 0) {
        const factor = 1 - damping * DT
        scale(self.vel, self.vel, factor > 0 ? factor : 0)
      }

      this.faceTravel(self, turnCos, turnSin)
      this.integrateWithCollision(self)
    }
  }

  /**
   * Turn the animal toward where it is trying to go, then make it travel the way it is pointing.
   *
   * **A sheep cannot strafe.** Before this, velocity was whatever the forces summed to, so a mob
   * under pressure slid sideways — legible, and nothing like an animal. Now the forces set a
   * *wanted* direction, the sheep turns toward it at a bounded rate, and it moves along its nose.
   * A flock therefore takes a beat to break in a new direction instead of changing velocity
   * instantly, and a mob turning away from the dog rolls rather than snapping.
   *
   * **No angle is ever formed.** `Math.atan2`/`sin`/`cos` are not correctly rounded and differ
   * between V8, SpiderMonkey and JSC, which would break replay verification across browsers. The
   * rotation is built from the perpendicular component and a cosine/sine pair that the caller
   * derives from a Taylor series in `+ − × ÷` — see `stepFlock`.
   */
  private faceTravel(self: Sheep, turnCos: number, turnSin: number): void {
    const speed = len(self.vel)
    // Standing still: keep facing wherever it was. A stopped animal does not spin.
    if (speed < 1e-6) return

    const wx = self.vel.x / speed
    const wy = self.vel.y / speed
    const h = self.heading
    const dot = h.x * wx + h.y * wy

    if (dot >= turnCos) {
      // Within a single tick's turn, so it can simply look where it is going.
      set(h, wx, wy)
    } else {
      // Rotate by exactly the cap, toward the wanted direction. The perpendicular component of the
      // want, relative to the current heading, is the axis to swing along.
      let px = wx - h.x * dot
      let py = wy - h.y * dot
      const pd = Math.sqrt(px * px + py * py)
      if (pd > 1e-9) {
        px /= pd
        py /= pd
      } else {
        // Exactly opposite: every way round is the same length, so pick one deterministically
        // rather than leaving the sheep unable to turn at all.
        px = -h.y
        py = h.x
      }
      const nx = h.x * turnCos + px * turnSin
      const ny = h.y * turnCos + py * turnSin
      // Renormalise: the Taylor cosine and sine are a few parts in a million short of the unit
      // circle, and a heading that is re-rotated every tick would drift without this.
      const nd = Math.sqrt(nx * nx + ny * ny)
      set(h, nx / nd, ny / nd)
    }

    // A standing animal shuffles. Only something actually travelling is committed to its line, and
    // that exemption is load-bearing rather than a nicety: with movement locked to the nose at every
    // speed, two sheep jammed together could no longer step apart, and the no-overlap pass lost
    // ground it had just won (worst separation 0.792 m → 0.703).
    if (speed < this.config.strafeBelowSpeed) return

    // Travel follows the nose, and a sheep pointing away from where it wants to be gives up speed
    // for the turn. Floored, because an animal still carries some way round a corner.
    const align = h.x * wx + h.y * wy
    const keep = align > this.config.turnSpeedFloor ? align : this.config.turnSpeedFloor
    set(self.vel, h.x * speed * keep, h.y * speed * keep)
  }

  private applyFenceAvoidance(self: Sheep): void {
    const cfg = this.config
    const avoid2 = cfg.fenceAvoidRadius * cfg.fenceAvoidRadius
    for (let f = 0; f < this.level.fences.length; f++) {
      const fence = this.level.fences[f]
      const d2 = closestPointOnSegment(self.pos, fence.a, fence.b, this.tmpA)
      if (d2 >= avoid2 || d2 === 0) continue
      sub(this.tmpB, self.pos, this.tmpA)
      const falloff = 1 - Math.sqrt(d2) / cfg.fenceAvoidRadius
      addScaled(self.acc, normalize(this.steer, this.tmpB), cfg.maxForce * cfg.fenceAvoidWeight * falloff)
    }
  }

  /**
   * Integrate position in sub-steps small enough that fence collision cannot be missed.
   *
   * Collision resolution is a discrete after-the-fact check: it looks at where a sheep ended up and
   * pushes it back out. That is only correct while a sheep cannot cross a fence within one step —
   * otherwise it lands on the far side and gets "resolved" the wrong way, i.e. tunnels straight
   * through. A single fixed step stopped being safe once sheep got fast (a panicked sheep now covers
   * ~0.8 m per tick against a 0.4 m body radius), so movement is split into sub-steps of at most
   * half a body radius each.
   *
   * The sub-step count is derived from speed, so it is deterministic and costs nothing when slow.
   */
  private integrateWithCollision(self: Sheep): void {
    const maxSubStep = this.config.sheepRadius * 0.5
    const displacement = len(self.vel) * DT
    const steps = displacement > maxSubStep ? Math.ceil(displacement / maxSubStep) : 1
    const subDt = DT / steps

    for (let k = 0; k < steps; k++) {
      addScaled(self.pos, self.vel, subDt)
      this.resolveFenceCollisions(self)
      this.clampToWorld(self)
    }
  }

  /** Push a sheep back out of any fence it overlaps, killing the velocity component into it. */
  private resolveFenceCollisions(self: Sheep): void {
    const r = this.config.sheepRadius
    const r2 = r * r
    for (let f = 0; f < this.level.fences.length; f++) {
      const fence = this.level.fences[f]
      const d2 = closestPointOnSegment(self.pos, fence.a, fence.b, this.tmpA)
      if (d2 >= r2) continue

      sub(this.tmpB, self.pos, this.tmpA)
      if (len2(this.tmpB) === 0) continue
      normalize(this.steer, this.tmpB)
      // Push out to exactly the body radius.
      set(this.tmpA, this.tmpA.x + this.steer.x * r, this.tmpA.y + this.steer.y * r)
      copy(self.pos, this.tmpA)
      // Kill the component of velocity heading into the fence; let the sheep slide along it.
      const vn = self.vel.x * this.steer.x + self.vel.y * this.steer.y
      if (vn < 0) addScaled(self.vel, this.steer, -vn)
    }
  }

  /**
   * Push overlapping bodies apart, so two sheep can never occupy the same ground.
   *
   * **Positional, and the only thing it takes from velocity is the approach between a pair — never
   * anything added.** Separation is
   * already a force; what it cannot do is guarantee anything, because a force competes with cohesion
   * and with the dog and can be outvoted. This is the hard floor underneath it. Doing it as an
   * impulse — the obvious way, and the way a physics engine would — would add speed to a flock that
   * is trying to come to rest, and settling is measured against `settleSpeed`: the win condition
   * would stop firing for a flock that is standing still and merely jostling. That failure took M1
   * to find once already.
   *
   * No physics engine is needed for this, and one would cost the thing the game is built on. The
   * whole correction is a pair sweep over the spatial grid that already exists: deterministic
   * because the grid is rebuilt in entity-index order and each pair is visited once, in index order,
   * with a symmetric half-correction each.
   *
   * Corrections are small and local, but they can still push a body into a fence or off the edge of
   * the paddock, so both of those are re-asserted afterwards — for the sheep that actually moved,
   * which on a typical tick is none of them.
   */
  private separateBodies(): void {
    const r = this.config.sheepRadius
    const minD = r * 2
    const minD2 = minD * minD

    for (let pass = 0; pass < BODY_MAX_PASSES; pass++) {
      let corrected = false
      for (let i = 0; i < this.sheep.length; i++) {
        const a = this.sheep[i]
        this.grid.forEachNeighbour(a.pos, (j) => {
          // Each pair exactly once, in index order, so the result cannot depend on which sheep the
          // sweep reached first.
          if (j <= i) return
          const b = this.sheep[j]
          let dx = b.pos.x - a.pos.x
          let dy = b.pos.y - a.pos.y
          const d2 = dx * dx + dy * dy
          if (d2 >= minD2 || d2 === 0) {
            if (d2 === 0) {
              // Exactly coincident, so there is no axis to separate along. The tie is broken from
              // the pair's indices rather than from the RNG: a draw here would shift the seeded
              // stream and re-roll every daily for a case that is otherwise invisible.
              const along = (i + j) % 2 === 0
              dx = along ? 1 : 0
              dy = along ? 0 : 1
              const half = r
              a.pos.x -= dx * half
              a.pos.y -= dy * half
              b.pos.x += dx * half
              b.pos.y += dy * half
              this.bodyMoved[i] = 1
              this.bodyMoved[j] = 1
                  corrected = true
            }
            return
          }
          const d = Math.sqrt(d2)
          const ux = dx / d
          const uy = dy / d
          const push = (minD - d) * 0.5
          a.pos.x -= ux * push
          a.pos.y -= uy * push
          b.pos.x += ux * push
          b.pos.y += uy * push

          // Take the APPROACH out of the pair's relative velocity, so they stop driving back into
          // each other on the next tick and the positional pass is not fighting the same collision
          // forever. This is the same move `resolveFenceCollisions` makes against a fence.
          //
          // **It can only ever remove speed, never add it**, which is what keeps it compatible with
          // a settle-based win condition — an impulse that pushed bodies apart would give a resting
          // flock energy and the pen could never be won. Sharing it evenly leaves the pair's centre
          // of mass moving exactly as it was.
          const vn = (b.vel.x - a.vel.x) * ux + (b.vel.y - a.vel.y) * uy
          if (vn < 0) {
            const half = vn * 0.5
            a.vel.x += ux * half
            a.vel.y += uy * half
            b.vel.x -= ux * half
            b.vel.y -= uy * half
          }
          this.bodyMoved[i] = 1
          this.bodyMoved[j] = 1
          corrected = true
        })
      }
      // Nothing overlapped on this sweep, so nothing will on the next one either.
      if (!corrected) break

      // Put the moved bodies back inside the fences and the paddock BEFORE the next sweep, so the
      // next one redistributes the jam along the obstruction instead of leaving a pair buried in
      // it. Doing this only at the end left sheep pressed into a wall overlapping by up to 90 mm.
      for (let i = 0; i < this.sheep.length; i++) {
        if (this.bodyMoved[i] === 0) continue
        this.bodyMoved[i] = 0
        this.resolveFenceCollisions(this.sheep[i])
        this.clampToWorld(this.sheep[i])
      }
    }
  }

  private clampToWorld(self: Sheep): void {
    const { w, h } = this.level.world
    const r = this.config.sheepRadius
    if (self.pos.x < r) {
      self.pos.x = r
      if (self.vel.x < 0) self.vel.x = 0
    } else if (self.pos.x > w - r) {
      self.pos.x = w - r
      if (self.vel.x > 0) self.vel.x = 0
    }
    if (self.pos.y < r) {
      self.pos.y = r
      if (self.vel.y < 0) self.vel.y = 0
    } else if (self.pos.y > h - r) {
      self.pos.y = h - r
      if (self.vel.y > 0) self.vel.y = 0
    }
  }

  private stepStress(): void {
    const cfg = this.config
    const dogRadius = cfg.dogRadius * (this.barking ? cfg.barkRadiusMultiplier : 1)
    const dogRadius2 = dogRadius * dogRadius
    const pressure = this.dogPressure
    // Two-phase: read all neighbour stress from the previous state, then commit. A single-phase
    // update would let contagion propagate across the whole flock within one tick, and would make
    // the result depend on array order.
    const next = new Array<number>(this.sheep.length)

    for (let i = 0; i < this.sheep.length; i++) {
      const self = this.sheep[i]
      let s = self.stress

      const dogD2 = dist2(self.pos, this.dog)
      if (dogD2 < dogRadius2) {
        const dd = Math.sqrt(dogD2)
        const falloff = 1 - dd / dogRadius

        /**
         * A dog in motion is a different animal from a dog standing there, and the rate-based term
         * above cannot express it: at speed the dog is inside a metre of a sheep for about two
         * ticks, which adds roughly 0.08 stress.
         *
         * **Scaled by the dog's SPEED, not by its closing speed** — and that correction is the whole
         * of this block's history. The first version used closing speed alone, which is zero at the
         * exact moment a dog sweeps *past* a sheep, because the velocity is then perpendicular to
         * the offset. It therefore nulled out the single most frightening thing that happens in a
         * paddock: measured, a dog passing 0.4 m from a sheep at full speed produced peak stress
         * 0.30 and a top sheep speed of 1.53 m/s. Nothing bolted, because the model could not see it.
         *
         * Direction still matters, but it modulates rather than gates: a dog bearing down is worse
         * than one sweeping past, which is worse than one running away — 1.0, 0.575 and 0.15 of the
         * speed respectively. Zero speed is still zero menace, so holding station, dropping and
         * standing at a fence stay silent, which is what keeps `drop` a real release.
         *
         * Quadratic in proximity, because fright is not linear in distance: a linear falloff makes
         * 1 m barely more alarming than 4 m.
         */
        const dogSpeed = Math.sqrt(
          this.dogVel.x * this.dogVel.x + this.dogVel.y * this.dogVel.y,
        )
        let menace = 0
        if (dogSpeed > 1e-9 && dd > 1e-9) {
          const cos =
            (this.dogVel.x * (self.pos.x - this.dog.x) +
              this.dogVel.y * (self.pos.y - this.dog.y)) /
            (dd * dogSpeed)
          menace = dogSpeed * (0.15 + 0.425 * (cos + 1))
        }
        const charge = cfg.stressChargeGain * falloff * falloff * menace * pressure
        // Temperament: a bold sheep takes less fright from the same dog, a timid one more. Applied
        // to fright only — never to decay — so a bold sheep is harder to frighten rather than
        // quicker to recover, which are different animals.
        const nerve = 1 - self.boldness * cfg.traitBoldness

        if (pressure === 1) {
          s += (cfg.stressGainRate * falloff + charge) * nerve * DT
        } else {
          // A lying dog lets the mob settle although it has not moved an inch. Gain is netted
          // against decay here rather than merely scaled, because a scaled gain with no decay
          // holds stress FLAT — and "lie down" that cannot calm sheep is not the command.
          //
          // The `pressure === 1` branch above is bit-for-bit the original path, deliberately: every
          // measured par and the pinned determinism hashes were taken on it, and an unconditional
          // reformulation would move them all for a case that is not even active.
          s += ((cfg.stressGainRate * falloff * pressure + charge) * nerve - cfg.stressDecayRate) * DT
        }
      } else {
        s -= cfg.stressDecayRate * DT
      }

      // Contagion: pulled toward the neighbour mean, with gain < 1 so the flock cannot latch.
      //
      // Its radius widens with fright on its own multiplier, split from the flocking one: how much
      // of the mob a sheep moves with and how far its fear carries are different questions, and
      // sharing `perceptionRadius` had them tuned as if they were the same one.
      const contagionR =
        cfg.perceptionRadius * (1 + (cfg.panicContagionMultiplier - 1) * self.stress)
      const contagion2 = contagionR * contagionR
      let sum = 0
      let count = 0
      this.grid.forEachNeighbour(self.pos, (j) => {
        if (j === i) return
        if (dist2(self.pos, this.sheep[j].pos) > contagion2) return
        sum += this.sheep[j].stress
        count++
      })
      if (count > 0) {
        const mean = sum / count
        if (mean > s) s += (mean - s) * cfg.stressContagion * DT
      }

      next[i] = clamp(s, 0, 1)
    }

    let total = 0
    for (let i = 0; i < this.sheep.length; i++) {
      const self = this.sheep[i]
      self.stress = next[i]
      total += self.stress
      // Hysteresis — a single threshold makes sheep flicker in and out of panic on the boundary.
      if (self.panicked) {
        if (self.stress < cfg.panicExit) self.panicked = false
      } else if (self.stress > cfg.panicEnter) {
        self.panicked = true
      }

      // Flight timer. Topped up (never truncated) while panicked, so sustained pressure keeps a
      // sheep running and the run-on measures the WORST fright it had, not the most recent tick.
      // Counted down otherwise. Integer ticks — no accumulation, nothing to drift.
      if (self.panicked) {
        const want = Math.round(cfg.flightTicksMax * self.stress)
        if (want > self.flightTicks) {
          self.flightTicks = want
          // Remember which way "away" was at the moment of the worst fright. A bolt is directed —
          // the sheep runs from the thing that frightened it, not in whatever direction it happened
          // to be pointing — and by the time it is running the dog may be long gone.
          const dx = self.pos.x - this.dog.x
          const dy = self.pos.y - this.dog.y
          const d = Math.sqrt(dx * dx + dy * dy)
          if (d > 1e-9) set(self.fleeDir, dx / d, dy / d)
        }
      } else if (self.flightTicks > 0) {
        self.flightTicks--
      }
    }
    this.stressAccumulator += this.sheep.length > 0 ? total / this.sheep.length : 0
  }

  private evaluate(): void {
    const gate = this.level.gate
    let penned = 0
    let speedTotal = 0
    for (let i = 0; i < this.sheep.length; i++) {
      const s = this.sheep[i]
      s.penned =
        s.pos.x >= gate.x &&
        s.pos.x <= gate.x + gate.w &&
        s.pos.y >= gate.y &&
        s.pos.y <= gate.y + gate.h
      if (s.penned) penned++
      speedTotal += len(s.vel)
    }

    const meanSpeed = this.sheep.length > 0 ? speedTotal / this.sheep.length : 0
    // Won only when every sheep is penned AND the flock has settled — otherwise a sheep bolting
    // straight back out through the gate would count as a win.
    if (penned === this.sheep.length && meanSpeed < this.config.settleSpeed) {
      this.settledFor++
      if (this.settledFor >= this.config.settleTicks) this.won = true
    } else {
      this.settledFor = 0
    }
  }

  /**
   * Turn this tick's state into fault EVENTS.
   *
   * Runs after `evaluate`, so `penned` is current — a pen escape is only visible by comparing the
   * count across the boundary where it is set.
   */
  private countFaults(pennedBefore: number): void {
    const cfg = this.config

    /**
     * Lost them from the pen — but only once the pen was actually FULL.
     *
     * The first version counted any drop in the penned total, and measured **4 escapes per run** on
     * clean bot runs. That is not a dog losing its stock; it is sheep jostling across the gate mouth
     * while they are being driven in, which is ordinary work. The trial fault is the pen being
     * filled and then broken, and here that has a precise meaning already: the win condition is all
     * N penned and settled, so an escape is the flock being complete and then not.
     */
    const pennedNow = this.pennedCount
    const full = pennedNow === this.sheep.length
    if (this.wasFullyPenned && !full) this.faults.penEscapes += pennedBefore - pennedNow
    this.wasFullyPenned = full

    // Rushed the stock: the flock crossing INTO panic, having been calm. The stress penalty already
    // charges the points; this makes them countable, so "−8 stress" becomes "rushed them 3 times".
    let total = 0
    for (let i = 0; i < this.sheep.length; i++) total += this.sheep[i].stress
    const mean = this.sheep.length > 0 ? total / this.sheep.length : 0
    if (this.wasCalm && mean > cfg.panicEnter) {
      this.faults.rushes++
      this.wasCalm = false
    } else if (!this.wasCalm && mean < cfg.panicExit) {
      this.wasCalm = true
    }

    // Bit the stock: close AND fast. Either alone is ordinary work — a dog stands close to hold, and
    // runs fast in open ground. It is the combination that is a dog going in at an animal.
    const fast =
      this.dogVel.x * this.dogVel.x + this.dogVel.y * this.dogVel.y >
      (cfg.dogSpeed * cfg.biteSpeedFraction) ** 2
    const bite2 = cfg.biteRadius * cfg.biteRadius
    for (let i = 0; i < this.sheep.length; i++) {
      const hit = fast && dist2(this.sheep[i].pos, this.dog) < bite2
      if (hit && !this.biting[i]) this.faults.bites++
      this.biting[i] = hit
    }

    // Crossed: the dog between the mob and the gate, close enough to matter.
    //
    // The destination stands in for the handler here — Big Dog has no handler entity, so the rule
    // is "do not get between the stock and where they are going" rather than "between the stock and
    // me". See § Trial scoring. Heavy but never fatal: this is a once-a-day puzzle and a DQ four
    // seconds in would simply end the day.
    let onGateSide = false
    if (this.workingCluster >= 0) {
      let ux = this.dog.x - this.workingCentre.x
      let uy = this.dog.y - this.workingCentre.y
      const ud = Math.sqrt(ux * ux + uy * uy)
      let gx = this.gateCentre.x - this.workingCentre.x
      let gy = this.gateCentre.y - this.workingCentre.y
      const gd = Math.sqrt(gx * gx + gy * gy)
      if (ud > 1e-6 && gd > 1e-6 && ud < cfg.dogRadius * 1.6) {
        onGateSide = (ux / ud) * (gx / gd) + (uy / ud) * (gy / gd) > 0.5
      }
    }
    if (onGateSide && !this.crossing) this.faults.crosses++
    this.crossing = onGateSide
  }

  get pennedCount(): number {
    let n = 0
    for (let i = 0; i < this.sheep.length; i++) if (this.sheep[i].penned) n++
    return n
  }

  /** FNV-1a over the exact bits of every sheep's position, velocity and stress, plus the dog. */
  stateHash(): string {
    const h = new StateHasher()
    h.mixU32(this.tick)
    for (let i = 0; i < this.sheep.length; i++) {
      const s = this.sheep[i]
      h.mixF64(s.pos.x).mixF64(s.pos.y)
      h.mixF64(s.vel.x).mixF64(s.vel.y)
      h.mixF64(s.stress)
    }
    h.mixF64(this.dog.x).mixF64(this.dog.y)
    return h.hex()
  }

  result(): RunResult {
    return {
      won: this.won,
      ticks: this.tick,
      elapsedMs: Math.round((this.tick / TICK_HZ) * 1000),
      barks: this.barks,
      meanStress: this.tick > 0 ? this.stressAccumulator / this.tick : 0,
      penned: this.pennedCount,
      flockSize: this.sheep.length,
      faults: { ...this.faults },
      stateHash: this.stateHash(),
    }
  }
}

/** Replay a full input log. Used by tests, the Big Dog bot, and server-side verification. */
export function replay(
  level: Level,
  seed: number,
  events: readonly InputEvent[],
  ticks: number,
  config: SimConfig = DEFAULT_CONFIG,
): Simulation {
  const sim = new Simulation(level, seed, config)
  // Bucket events by tick once, rather than scanning the log every tick.
  const byTick = new Map<number, InputEvent[]>()
  for (const ev of events) {
    const bucket = byTick.get(ev.tick)
    if (bucket) bucket.push(ev)
    else byTick.set(ev.tick, [ev])
  }
  for (let t = 0; t < ticks && !sim.won; t++) {
    sim.step(byTick.get(t) ?? [])
  }
  return sim
}
